import { reportService } from "@/services/api/report.service";
import { reportModel, isReportEditable } from "@/models/api/report.model";
import { alertHelper, sharedHelper, sessionHelper } from "@/utils";
import i18n from "@/i18n";

export const REPORT_MODULE = "api/report";
export const GET_ALL_REPORT = "getAllReport";
export const GET_REPORT = "getReport";
export const CREATE_REPORT = "createReport";
export const UPDATE_REPORT = "updateReport";
export const UPDATE_REPORT_STATUS = "updateReportStatus";
export const SOFT_DELETE_REPORT = "softDeleteReport";
export const LOAD_REPORT_OPTIONS = "loadReportOptions";

export const state = {
  list: { data: [], pagination: { pageNumber: 1, pageSize: 10, total: 0 } },
  details: null,
  status: { list: true, detail: true, save: true, options: true },
  errors: { list: "", detail: "", save: "", options: "" },
  options: { users: [] },
  listRequest: 0,
};

export const actions = {
  async [SOFT_DELETE_REPORT]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const response = await reportService.softDelete(data);
      if (!response || response.success === false)
        throw new Error(i18n.t("message.unknownError"));
      alertHelper.alertSuccess({
        message: i18n.t("message.deleteXSuccess", [i18n.t("module.report")]),
      });
      await dispatch(GET_ALL_REPORT, { filter });
      return true;
    } catch (error) {
      const msg = sharedHelper.getErrorMessage(error);
      alertHelper.alertError({ message: msg, title: i18n.t("message.error") });
      commit("failed", { type: "save", error: msg });
      return null;
    } finally {
      commit("finished", "save");
    }
  },
  async [LOAD_REPORT_OPTIONS]({ commit }) {
    commit("requesting", "options");
    try {
      const options = await reportService.getOptions();
      commit("optionsLoaded", options);
      return options;
    } catch (error) {
      commit("failed", {
        type: "options",
        error: sharedHelper.getErrorMessage(error),
      });
      return null;
    } finally {
      commit("finished", "options");
    }
  },
  async [GET_ALL_REPORT]({ commit, state }, { filter } = {}) {
    commit("requesting", "list");
    const request = state.listRequest;
    try {
      const response = await reportService.getAll(filter);
      if (!response) throw new Error(i18n.t("message.unknownError"));
      if (request === state.listRequest) commit("listed", response);
      return response?.data || response;
    } catch (error) {
      if (request === state.listRequest)
        commit("failed", {
          type: "list",
          error: sharedHelper.getErrorMessage(error),
        });
      return null;
    } finally {
      if (request === state.listRequest) commit("finished", "list");
    }
  },
  async [GET_REPORT]({ commit }, { uuid }) {
    commit("requesting", "detail");
    try {
      const response = await reportService.show(uuid);
      if (!response) throw new Error(i18n.t("message.unknownError"));
      const result = response?.data || response;
      commit("detailed", result);
      return result;
    } catch (error) {
      commit("failed", {
        type: "detail",
        error: sharedHelper.getErrorMessage(error),
      });
      return null;
    } finally {
      commit("finished", "detail");
    }
  },
  async [CREATE_REPORT]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const response = await reportService.store(data);
      if (!response) throw new Error(i18n.t("message.unknownError"));
      const result = response?.data || response;
      alertHelper.alertSuccess({
        message: i18n.t("message.createXSuccess", [i18n.t("module.report")]),
      });
      await dispatch(GET_ALL_REPORT, { filter });
      return result;
    } catch (error) {
      const msg = sharedHelper.getErrorMessage(error);
      alertHelper.alertError({ message: msg, title: i18n.t("message.error") });
      commit("failed", { type: "save", error: msg });
      return null;
    } finally {
      commit("finished", "save");
    }
  },
  async [UPDATE_REPORT]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const details = await reportService.show(data.uuid);
      if (!details || details.success === false)
        throw new Error(i18n.t("message.unknownError"));
      const report = details.data || details;
      if (!isReportEditable(report)) return null;
      const response = await reportService.update({
        ...data,
        userUUID: report.user.uuid,
      });
      if (!response) throw new Error(i18n.t("message.unknownError"));
      const result = response?.data || response;
      alertHelper.alertSuccess({
        message: i18n.t("message.updateXSuccess", [i18n.t("module.report")]),
      });
      await dispatch(GET_ALL_REPORT, { filter });
      return result;
    } catch (error) {
      const msg = sharedHelper.getErrorMessage(error);
      alertHelper.alertError({ message: msg, title: i18n.t("message.error") });
      commit("failed", { type: "save", error: msg });
      return null;
    } finally {
      commit("finished", "save");
    }
  },
  async [UPDATE_REPORT_STATUS]({ commit, dispatch }, { data, filter }) {
    if (sessionHelper.getPermissionLevel("developer") !== 2) return null;
    commit("requesting", "save");
    try {
      const response = await reportService.updateStatus(data);
      if (!response) throw new Error(i18n.t("message.unknownError"));
      const result = response?.data || response;
      alertHelper.alertSuccess({
        message: i18n.t("message.updateStatusSuccess"),
      });
      await dispatch(GET_ALL_REPORT, { filter });
      return result;
    } catch (error) {
      const msg = sharedHelper.getErrorMessage(error);
      alertHelper.alertError({ message: msg, title: i18n.t("message.error") });
      commit("failed", { type: "save", error: msg });
      return null;
    } finally {
      commit("finished", "save");
    }
  },
};

export const mutations = {
  optionsLoaded(state, options) {
    state.options = options || { users: [] };
  },
  requesting(state, type) {
    state.status[type] = false;
    state.errors[type] = "";
    if (type === "list") state.listRequest++;
    if (type === "detail") state.details = null;
  },
  finished(state, type) {
    state.status[type] = true;
  },
  failed(state, { type, error }) {
    state.errors[type] = error;
  },
  clearSaveError(state) {
    state.errors.save = "";
  },
  listed(state, result) {
    const list = Array.isArray(result)
      ? result
      : Array.isArray(result?.data)
      ? result.data
      : [];
    state.list.data = list.map(reportModel);
    const pagination =
      !Array.isArray(result) && result?.pagination ? result.pagination : {};
    state.list.pagination = sharedHelper.apiPaginationAdapter(pagination);
  },
  detailed(state, result) {
    const detail = result && result.data && !result.uuid ? result.data : result;
    state.details = detail ? reportModel(detail) : null;
  },
};
